import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'

interface RequireAuthProps {
  children: React.ReactNode
}

/** Supabase session gate. Renders children when signed in; otherwise bounces to
 * /login, preserving `pathname + search` so query-param entry points survive
 * the auth round-trip. */
export function RequireAuth({ children }: RequireAuthProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading, initialized, initialize } = useAuthStore()

  // Initialize auth on mount.
  useEffect(() => {
    initialize()
  }, [initialize])

  // Redirect to login once we know there's no session.
  useEffect(() => {
    if (initialized && !loading && !user) {
      const returnTo = encodeURIComponent(location.pathname + location.search)
      navigate(`/login?returnTo=${returnTo}`, { replace: true })
    }
  }, [initialized, loading, user, navigate, location.pathname, location.search])

  // Loading state.
  if (!initialized || loading) {
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-6 h-6 border border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground font-light">Loading...</p>
        </div>
      </div>
    )
  }

  // Not authenticated — the redirect effect above is in flight.
  if (!user) {
    return null
  }

  return <>{children}</>
}
